import { type ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Spinner } from '@/components/spinner';
import { cn } from '@/lib/cn';

export interface DataTableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Render the cell content. */
  cell: (row: Row, index: number) => ReactNode;
  className?: string;
  headClassName?: string;
  /** Optional horizontal alignment for the cell + header. */
  align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<Row> {
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  onRowClick?: (row: Row) => void;
  /** Rendered when there are no rows. */
  emptyState?: ReactNode;
  /** Rendered as a header above the table; great place for filters. */
  toolbar?: ReactNode;
  /** Show a top-of-table loading bar overlay. */
  loading?: boolean;
  /** Optional caption — announced by screen readers. */
  caption?: string;
  className?: string;
}

const ALIGN_CLASS = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyState,
  toolbar,
  loading = false,
  caption,
  className,
}: DataTableProps<Row>): JSX.Element {
  const isEmpty = rows.length === 0 && !loading;

  if (isEmpty && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card',
        className,
      )}
    >
      {toolbar ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border/70 bg-card/60 px-3 py-2">
          {toolbar}
        </div>
      ) : null}

      <div className="relative overflow-x-auto">
        {loading ? (
          <div className="absolute inset-x-0 top-0 z-10 flex h-1 items-stretch overflow-hidden">
            <span className="h-full w-1/3 animate-shimmer bg-gradient-to-r from-transparent via-primary/70 to-transparent bg-[length:200%_100%]" />
          </div>
        ) : null}
        <Table>
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <TableHeader>
            <TableRow className="border-b border-border bg-muted/40 hover:bg-muted/40">
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={cn(
                    'h-10 text-xs font-medium uppercase tracking-wide text-muted-foreground',
                    c.align && ALIGN_CLASS[c.align],
                    c.headClassName,
                  )}
                >
                  {c.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-border/60 transition-colors last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-accent/40',
                )}
              >
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={cn(
                      'py-3 text-sm text-foreground',
                      c.align && ALIGN_CLASS[c.align],
                      c.className,
                    )}
                  >
                    {c.cell(row, i)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner /> Loading…
                    </span>
                  ) : (
                    'Nothing to show yet.'
                  )}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
